<template>
  <div
    class="relative group transition-height bg-gray-800 truncate"
    v-show="!sessionStore.userIsObserver"
    :class="!isMinimized && isMinimizable ? 'h-32 border' : !isMinimizable ? 'h-full' : 'h-8'"
  >
    <div v-if="isMinimizable">
      <div
        class="
          h-full
          w-full
          absolute
          z-10
          transition-all
          bg-opacity-0 bg-black
          group-hover:bg-opacity-60
        "
        v-if="!isMinimized"
      >
        <ChevronDownIcon
          @click="toggleMinimizeVideo"
          class="
            absolute
            text-white
            w-6
            h-6
            opacity-0
            group-hover:opacity-100
            top-2
            right-2
            cursor-pointer
          "
        ></ChevronDownIcon>
      </div>

      <div class="w-full h-11 bg-gray-800" v-else>
        <ChevronUpIcon
          @click="toggleMinimizeVideo"
          class="absolute text-white bg-gray-800 z-50 w-8 h-8 p-1 right-1 cursor-pointer"
        ></ChevronUpIcon>
      </div>
    </div>

    <div v-show="isVideoOn" class="h-full w-full relative">
      <ViceLoader />
      <video
        muted
        autoplay
        playsinline
        :class="['w-full h-full object-cover', showControls && 'rounded-tl-lg rounded-bl-lg']"
        ref="videoRef"
      ></video>
    </div>

    <!-- Video off -->
    <div
      :class="[
        'flex justify-center items-center h-full w-full bg-vice-medium-teal',
        showControls && 'rounded-tl-lg rounded-bl-lg',
      ]"
      v-show="isVideoOff"
    >
      <VideoOff
        :name="sessionStore.authUser?.name"
        :class="{ 'sm:w-16 sm:h-16 sm:text-xl': isMinimizable }"
        :minimizable="isMinimizable"
        :profile_photo_url="sessionStore.authUser?.profile_photo_url"
      />
    </div>

    <audio
      autoplay
      muted
      ref="audioRef"
      v-show="multiCameraDevicesStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isMicrophoneOn"
    ></audio>

    <UserLabel
      :class="{ 'bottom-1 right-9': isMinimized }"
      :localStats="statsValue"
      :showControls="showControls"
      :displayName="sessionStore.authUser?.name"
      :raisedHand="sessionStore.isHandRaised(sessionStore.authUser?.id)"
      :isMutedMicrophone="
        !multiCameraDevicesStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isMicrophoneOn
      "
      v-if="showTitle"
    />

    <!--    <section class="absolute bottom-0 right-0 text-left" v-show="showControls">-->
    <!--      &lt;!&ndash; TODO: adapt new designs to these buttons &ndash;&gt;-->
    <!--      <button data-cy="open-external-camera-settings" class="block p-4" title="Switch Stream">-->
    <!--        <CogIcon class="h-6 w-6 mr-2 shadow-filter" @click="openAVSettingsModal" />-->
    <!--      </button>-->
    <!--    </section>-->
  </div>
</template>

<script>
// import { CogIcon } from '@heroicons/vue/solid';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/outline';
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import VideoOff from '@/Components/Session/Grid/VideoOff.vue';
import { CAMERA_TYPES } from '@/Utils/vice-constants';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';
import useSessionStore from '@/Stores/useSessionStore';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import JitsiService from '@/Services/JitsiService';
import UserLabel from '@/Components/Session/Grid/UserLabel.vue';

const Jitsi = JitsiService();

export default {
  name: 'StreamLocalPlayer',
  props: {
    showControls: {
      type: Boolean,
      default: false,
    },
    showTitle: {
      type: Boolean,
      default: false,
    },
    isMinimizable: {
      type: Boolean,
      default: false,
    },
    statsValue: {
      type: Number,
      default: 0,
    },
  },
  components: {
    VideoOff,
    // CogIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    ViceLoader,
    UserLabel,
  },

  setup(props) {
    const isMinimized = ref(false);
    const sessionStore = useSessionStore();
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();

    const videoRef = ref(null);
    const audioRef = ref(null);

    const openAVSettingsModal = () => {
      multiCameraDevicesStore.setCurrentActiveCameraType(CAMERA_TYPES.INTERNAL_WEBCAM);
      multiCameraDevicesStore.openAVSettingsModal();
    };

    const isVideoOn = computed(
      () =>
        multiCameraDevicesStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isCameraOn &&
        !isMinimized.value,
    );

    const isVideoOff = computed(
      () =>
        !multiCameraDevicesStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isCameraOn &&
        !isMinimized.value,
    );

    const toggleUserCamera = async () => {
      await multiCameraDevicesStore.toggleCamera(CAMERA_TYPES.INTERNAL_WEBCAM);
    };

    const toggleUserMicrophone = async () => {
      await multiCameraDevicesStore.toggleMicrophone(CAMERA_TYPES.INTERNAL_WEBCAM);
    };

    const toggleMinimizeVideo = () => {
      isMinimized.value = !isMinimized.value;
    };

    // Maximize the preview when the screenshare stops on the clinician side
    watch(
      () => props.isMinimizable,
      () => {
        isMinimized.value = false;
      },
    );

    watch(videoRef, (newVideoRef) => {
      if (sessionStore.userIsObserver) return;
      if (newVideoRef) {
        multiCameraDevicesStore.setVideoRef(newVideoRef, CAMERA_TYPES.INTERNAL_WEBCAM);
      }
    });

    watch(audioRef, async (newAudioRef) => {
      if (sessionStore.userIsObserver) return;
      if (newAudioRef) {
        multiCameraDevicesStore.setAudioRef(newAudioRef, CAMERA_TYPES.INTERNAL_WEBCAM);

        try {
          await Jitsi.changeAudioOutput(
            multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].speakerDeviceId,
          );
          if (newAudioRef.sinkId) {
            await newAudioRef.setSinkId(
              multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].speakerDeviceId,
            );
          }
        } catch (error) {
          console.error({ error });
        }
      }
    });

    onMounted(async () => {
      if (sessionStore.userIsObserver) return;
      await multiCameraDevicesStore.activateMedia(CAMERA_TYPES.INTERNAL_WEBCAM);
    });

    onUnmounted(async () => {
      if (sessionStore.userIsObserver) return;
      await multiCameraDevicesStore.endCall();
    });

    return {
      videoRef,
      audioRef,
      isVideoOn,
      isVideoOff,
      isMinimized,
      sessionStore,
      multiCameraDevicesStore,
      toggleUserCamera,
      toggleMinimizeVideo,
      openAVSettingsModal,
      toggleUserMicrophone,
      CAMERA_TYPES,
    };
  },
};
</script>

<style scoped>
video {
  transform: scale(-1, 1); /*For Firefox (& IE) */
  -webkit-transform: scale(-1, 1); /*for Chrome & Opera (& Safari) */
}

.text-shadow {
  text-shadow: 0 0 2px black;
}

.shadow-filter {
  filter: drop-shadow(0 0 2px black);
}
</style>
